import { useState } from "react";
import type { PublicEvent } from "@vedaunion/shared";
import { eventIcsHref } from "../../lib/events";

/**
 * Small "add to calendar" + share rail. Renders deep-links for Google
 * Calendar, Outlook web, plus a vanilla `.ics` download (which Apple Calendar
 * + most desktop clients consume), plus a copy-link button using the
 * Clipboard API with a short-lived "Copied" confirmation.
 */
function IcoCalendar() {
  return (
    <svg aria-hidden width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="14" height="12" rx="1" />
      <line x1="1" y1="7" x2="15" y2="7" />
      <line x1="5" y1="1" x2="5" y2="5" />
      <line x1="11" y1="1" x2="11" y2="5" />
    </svg>
  );
}

function IcoPin() {
  return (
    <svg aria-hidden width="12" height="14" viewBox="0 0 12 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3" />
      <path d="M6 9v6M3 15h6" />
    </svg>
  );
}

function IcoLink() {
  return (
    <svg aria-hidden width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 9.5a4 4 0 0 0 5.657 0l2-2a4 4 0 0 0-5.657-5.657l-1 1" />
      <path d="M9.5 6.5a4 4 0 0 0-5.657 0l-2 2a4 4 0 0 0 5.657 5.657l1-1" />
    </svg>
  );
}
export function EventActions({ event }: { event: PublicEvent }) {
  const [copied, setCopied] = useState<string | null>(null);

  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const detailsParts = [event.summary, event.description].filter(Boolean).join("\n\n");
  const eventUrl = typeof window !== "undefined" ? `${window.location.origin}/calendar/${event.slug}` : "";

  const googleHref = (() => {
    const base = "https://calendar.google.com/calendar/render";
    const sp = new URLSearchParams({
      action: "TEMPLATE",
      text: event.title,
      dates: `${fmt(start)}/${fmt(end)}`,
      details: detailsParts,
      location: event.location ?? event.onlineUrl ?? "",
      ctz: event.timezone,
    });
    return `${base}?${sp.toString()}`;
  })();

  const outlookHref = (() => {
    const base = "https://outlook.live.com/calendar/0/deeplink/compose";
    const sp = new URLSearchParams({
      path: "/calendar/action/compose",
      rru: "addevent",
      subject: event.title,
      startdt: start.toISOString(),
      enddt: end.toISOString(),
      body: detailsParts,
      location: event.location ?? event.onlineUrl ?? "",
    });
    return `${base}?${sp.toString()}`;
  })();

  const onCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      window.prompt("Copy this URL:", text);
    }
  };

  return (
    <div className="event-actions">
      <a className="event-action" href={eventIcsHref(event.slug)}>
        <IcoCalendar />
        Download .ics
      </a>
      <a className="event-action" href={googleHref} target="_blank" rel="noopener noreferrer">
        <IcoCalendar />
        Google Calendar
      </a>
      <a className="event-action" href={outlookHref} target="_blank" rel="noopener noreferrer">
        <IcoCalendar />
        Outlook
      </a>
      {event.location && (
        <a
          className="event-action"
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <IcoPin />
          Map
        </a>
      )}
      <button type="button" className="event-action" onClick={() => onCopy(eventUrl, "link")}>
        <IcoLink />
        {copied === "link" ? "Copied" : "Copy link"}
      </button>
    </div>
  );
}
